import { format } from 'date-fns';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Download, Clock, Wrench, FileCheck2, Moon } from 'lucide-react';
import { usePayrollEmployeeDetail, PayrollEmployeeDetail } from '@/hooks/usePayrollEmployeeDetail';
import { formatMinutesAsHours } from '@/utils/payrollDateUtils';
import { PayrollSummaryRow } from '@/hooks/usePayrollSummary';

interface EmployeeDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  employee: PayrollSummaryRow | null;
  shopId?: string;
  startDate: Date;
  endDate: Date;
}

export function EmployeeDetailDrawer({
  open,
  onClose,
  employee,
  shopId,
  startDate,
  endDate,
}: EmployeeDetailDrawerProps) {
  const { data: detail, isLoading } = usePayrollEmployeeDetail({
    shopId,
    employeeId: employee?.employee_id,
    employeeType: employee?.employee_type,
    startDate,
    endDate,
    enabled: open && !!employee,
  });

  const handleExportEmployee = () => {
    if (!employee || !detail) return;

    const headers = ['Date', 'Clock In', 'Clock Out', 'Hours', 'After Hours', 'Approved By', 'Notes'];
    const rows = detail.clock_entries.map(entry => [
      format(new Date(entry.clock_in_time), 'MM/dd/yyyy'),
      format(new Date(entry.clock_in_time), 'HH:mm'),
      entry.clock_out_time ? format(new Date(entry.clock_out_time), 'HH:mm') : 'Active',
      formatMinutesAsHours(entry.overlap_minutes),
      entry.is_after_hours ? 'Yes' : 'No',
      entry.approver_name || '—',
      entry.notes || '',
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${employee.employee_name.replace(/\s+/g, '-')}-timesheet-${format(startDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {employee?.employee_name}
            <Badge variant="secondary">{employee?.role_label}</Badge>
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">Loading details...</div>
        ) : detail ? (
          <div className="space-y-6 mt-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Clock className="h-4 w-4" />
                    Clock Hours
                  </div>
                  <div className="text-xl font-bold">
                    {formatMinutesAsHours(employee?.clock_minutes || 0)}
                  </div>
                </CardContent>
              </Card>

              {employee?.employee_type === 'technician' && (
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                      <Wrench className="h-4 w-4" />
                      Produced Hours
                    </div>
                    <div className="text-xl font-bold">
                      {formatMinutesAsHours(employee?.produced_minutes || 0)}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Produced Summary for Technicians */}
            {employee?.employee_type === 'technician' && detail.produced_summary && (
              <>
                <Separator />
                <div>
                  <h4 className="font-semibold mb-3">Production Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Approved Hours:</span>
                      <span className="font-medium">{formatMinutesAsHours(detail.produced_summary.booked_minutes)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completed Tickets:</span>
                      <span className="font-medium">{detail.produced_summary.ticket_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Actual Hours (timers):</span>
                      <span className="font-medium">{formatMinutesAsHours(detail.produced_summary.wrench_minutes)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Timer Sessions:</span>
                      <span className="font-medium">{detail.produced_summary.session_count}</span>
                    </div>
                    {/* oct20e LABOR: the shop's own stored pay basis (OSH-282), shown only when one was recorded */}
                    {detail.produced_summary.pay_basis_minutes != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pay Basis Hours:</span>
                        <span className="font-medium">{formatMinutesAsHours(detail.produced_summary.pay_basis_minutes)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Clock Entries Table */}
            <Separator />
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">Clock Entries</h4>
                <Button variant="outline" size="sm" onClick={handleExportEmployee}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>

              {detail.clock_entries.length === 0 ? (
                <div className="text-center text-muted-foreground py-4">
                  No clock entries in this period
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>In</TableHead>
                        <TableHead>Out</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.clock_entries.map(entry => (
                        <TableRow key={entry.id}>
                          <TableCell>
                            {format(new Date(entry.clock_in_time), 'MMM d')}
                          </TableCell>
                          <TableCell>
                            {format(new Date(entry.clock_in_time), 'h:mm a')}
                          </TableCell>
                          <TableCell>
                            {entry.clock_out_time 
                              ? format(new Date(entry.clock_out_time), 'h:mm a')
                              : <Badge variant="default">Active</Badge>
                            }
                          </TableCell>
                          <TableCell className="text-right">
                            {formatMinutesAsHours(entry.overlap_minutes)}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {entry.is_after_hours && (
                                <Badge variant="secondary" className="gap-1">
                                  <Moon className="h-3 w-3" />
                                  After
                                </Badge>
                              )}
                              {entry.approved_by && (
                                <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
                                  <FileCheck2 className="h-3 w-3" />
                                  Reviewed
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
